import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { ThemeEvents, PersonalizationUpdateEvent, ProductSummaryUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductSummaryRefs
 * @property {HTMLElement} total
 * @property {HTMLElement} compareAt
 * @property {HTMLElement} compareAtTotal
 * @property {HTMLElement} lines
 * @property {HTMLElement} mainLine
 * @property {HTMLTemplateElement} addOnLineTemplate
 */

/**
 * Keeps the order summary on the selected variant, selling plan, quantity and add-ons. A variant change re-renders
 * only the variant picker, so the summary is taken from the section it fetched. The product counts at the chosen
 * quantity and each checked add-on once, at its discounted price while the free add-on promotion covers it. A saved
 * personalization that applies adds its label to the product's options and prices the product at its personalized
 * variant. Each recalculation announces the total.
 *
 * @extends {Component<ProductSummaryRefs>}
 */
class ProductSummary extends Component {
  requiredRefs = ['total', 'compareAt', 'compareAtTotal', 'lines', 'mainLine', 'addOnLineTemplate'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | null} The personalization's label while it applies. */
  #personalizationLabel = null;

  /** @type {{ price: number, compareAtPrice: number } | null} The personalized variant's prices while it applies. */
  #personalizationPrices = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const container = this.closest('.shopify-section, dialog');
    container?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    container?.addEventListener('change', this.#handleChange, { signal });
    container?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#update, { signal });
    container?.addEventListener(ThemeEvents.personalizationUpdate, this.#handlePersonalizationUpdate, { signal });

    // The add-ons settle their free add-on marks when they connect, which may come after this
    requestAnimationFrame(this.#update);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    const form = document.getElementById(this.dataset.formId ?? '');
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * The add-on checkboxes ticked for the product form, in page order.
   * @returns {HTMLInputElement[]}
   */
  get #checkedAddOns() {
    const inputs = this.#form ? Array.from(this.#form.elements) : [];

    return inputs.filter(
      (input) => input instanceof HTMLInputElement && input.matches('[data-add-on-input]:checked')
    );
  }

  /**
   * @param {Event} event - The change event.
   */
  #handleChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-add-on-input], [name="quantity"]')) return;

    this.#update();
  };

  /**
   * Writes the total, the compare-at total and the add-on lines.
   */
  #update = () => {
    const quantityInput = this.#form?.elements.namedItem('quantity');
    const quantity = quantityInput instanceof HTMLInputElement ? Math.max(Number(quantityInput.value) || 1, 1) : 1;
    // The personalized variant is bought in place of the selected one
    const prices = this.#personalizationPrices;
    const price = prices ? prices.price : Number(this.dataset.price) || 0;
    const compareAtPrice = Math.max((prices ? prices.compareAtPrice : Number(this.dataset.compareAtPrice)) || 0, price);
    const addOns = this.#checkedAddOns;

    const addOnTotal = addOns.reduce((sum, input) => {
      const isDiscounted = Boolean(input.closest('[data-discounted]')) && input.dataset.discountedPrice !== undefined;
      return sum + Number(isDiscounted ? input.dataset.discountedPrice : input.dataset.price);
    }, 0);

    const total = price * quantity + addOnTotal;
    const compareAtTotal = compareAtPrice * quantity + addOnTotal;
    const { total: totalElement, compareAt, compareAtTotal: compareAtTotalElement } = this.refs;

    totalElement.textContent = this.#formatMoney(total);
    compareAtTotalElement.textContent = this.#formatMoney(compareAtTotal);
    compareAt.hidden = compareAtTotal <= total;

    this.#renderAddOnLines(addOns);
    this.#renderPersonalization();
    this.dispatchEvent(new ProductSummaryUpdateEvent(total));
  };

  /**
   * @param {PersonalizationUpdateEvent} event - The personalization update event.
   */
  #handlePersonalizationUpdate = (event) => {
    this.#personalizationLabel = event.detail.label;
    this.#personalizationPrices = event.detail.prices;
    this.#update();
  };

  /**
   * Appends the personalization's label to the product's options while it applies, adding the options row when the
   * product has none.
   */
  #renderPersonalization() {
    const { mainLine } = this.refs;

    for (const element of mainLine.querySelectorAll('[data-personalization-option]')) element.remove();

    if (!this.#personalizationLabel) return;

    let options = mainLine.querySelector('.product-summary__options');
    if (!options) {
      options = document.createElement('p');
      options.className = 'product-summary__options';
      options.toggleAttribute('data-personalization-option', true);
      mainLine.append(options);
    }

    const option = document.createElement('span');
    option.className = 'product-summary__option';
    option.toggleAttribute('data-personalization-option', true);
    option.textContent = this.#personalizationLabel;
    options.append(option);
  }

  /**
   * Replaces the add-on lines with one per checked add-on.
   * @param {HTMLInputElement[]} addOns - The checked add-on checkboxes.
   */
  #renderAddOnLines(addOns) {
    const { lines, addOnLineTemplate } = this.refs;

    for (const line of lines.querySelectorAll('[data-add-on-line]')) line.remove();

    for (const input of addOns) {
      const fragment = /** @type {DocumentFragment} */ (addOnLineTemplate.content.cloneNode(true));
      const title = fragment.querySelector('.product-summary__title');
      const options = fragment.querySelector('.product-summary__options');

      if (title) title.textContent = input.dataset.summaryTitle ?? '';

      /** @type {string[]} */
      const optionValues = input.dataset.summaryOptions ? JSON.parse(input.dataset.summaryOptions) : [];
      for (const optionValue of optionValues) {
        const option = document.createElement('span');
        option.className = 'product-summary__option';
        option.textContent = optionValue;
        options?.append(option);
      }

      lines.append(fragment);
    }
  }

  /**
   * @param {number} amount - The amount in minor units.
   * @returns {string}
   */
  #formatMoney(amount) {
    return formatMoney(amount, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? '');
  }

  /**
   * Re-renders the summary for the selected variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        // A morphed element, such as `<main>` for a combined listing sibling, already carries the new summary,
        // which still lacks the add-ons the sibling checks by default
        if (detail.morphElementSelector) {
          // The previous product's personalization does not carry over
          this.#personalizationLabel = null;
          this.#personalizationPrices = null;
          requestAnimationFrame(this.#update);
          return;
        }

        const newSummary = detail.html.querySelector(`product-summary[data-block-id="${this.dataset.blockId}"]`);
        if (!newSummary) return;

        morph(this, newSummary, MORPH_OPTIONS);
        // The morph leaves the element's own attributes alone
        this.dataset.price = newSummary.getAttribute('data-price') ?? '';
        this.dataset.compareAtPrice = newSummary.getAttribute('data-compare-at-price') ?? '';
        // The add-ons re-render and reset their selection from the same event, in either order
        requestAnimationFrame(this.#update);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('[product-summary] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-summary')) {
  customElements.define('product-summary', ProductSummary);
}
